import {WarehouseLogin, CreatedWarehouseLogin} from 'constants/WarehouseConstants';
import notifications, {Response} from 'notifications';
import * as popup from 'Popup';
import {BenchlingDispatch, Thunk} from 'redux-stores/Store';
import * as net from 'util/Net';

import CreateUserDefinedViewException from './CreateUserDefinedViewException';
import * as WarehouseDAO from './WarehouseDAO';

export enum WarehouseActionType {
  WAREHOUSE_LOGIN_LIST_COMPLETE = 'WAREHOUSE_LOGIN_LIST_COMPLETE',
  WAREHOUSE_LOGIN_REVOKE_START = 'WAREHOUSE_LOGIN_REVOKE_START',
  WAREHOUSE_LOGIN_REVOKE_FAIL = 'WAREHOUSE_LOGIN_REVOKE_FAIL',
  WAREHOUSE_CREATE_LOGIN_MODAL_OPEN = 'WAREHOUSE_CREATE_LOGIN_MODAL_OPEN',
  WAREHOUSE_CREATE_LOGIN_MODAL_CLOSE = 'WAREHOUSE_CREATE_LOGIN_MODAL_CLOSE',
  WAREHOUSE_CREATE_LOGIN_LABEL_UPDATE = 'WAREHOUSE_CREATE_LOGIN_LABEL_UPDATE',
  WAREHOUSE_CREATE_LOGIN_MODAL_CREATE_START = 'WAREHOUSE_CREATE_LOGIN_MODAL_CREATE_START',
  WAREHOUSE_CREATE_LOGIN_MODAL_CREATE_COMPLETE = 'WAREHOUSE_CREATE_LOGIN_MODAL_CREATE_COMPLETE',
  WAREHOUSE_CREATE_LOGIN_MODAL_CREATE_FAIL = 'WAREHOUSE_CREATE_LOGIN_MODAL_CREATE_FAIL',
}

interface WarehouseLoginListCompleteAction {
  type: WarehouseActionType.WAREHOUSE_LOGIN_LIST_COMPLETE;
  logins: Array<WarehouseLogin>;
}

function listComplete(logins: Array<WarehouseLogin>): WarehouseLoginListCompleteAction {
  return {
    type: WarehouseActionType.WAREHOUSE_LOGIN_LIST_COMPLETE,
    logins,
  };
}

interface WarehouseLoginRevokeStartAction {
  type: WarehouseActionType.WAREHOUSE_LOGIN_REVOKE_START;
  login: WarehouseLogin;
}

function revokeStart(login: WarehouseLogin): WarehouseLoginRevokeStartAction {
  return {
    type: WarehouseActionType.WAREHOUSE_LOGIN_REVOKE_START,
    login,
  };
}

interface WarehouseLoginRevokeFailAction {
  type: WarehouseActionType.WAREHOUSE_LOGIN_REVOKE_FAIL;
  login: WarehouseLogin;
}

function revokeFail(login: WarehouseLogin): WarehouseLoginRevokeFailAction {
  return {
    type: WarehouseActionType.WAREHOUSE_LOGIN_REVOKE_FAIL,
    login,
  };
}

export function fetchLogins(): Thunk<void> {
  return (dispatch: BenchlingDispatch) =>
    WarehouseDAO.fetchLogins().then(
      ({logins}: {logins: Array<WarehouseLogin>}) => {
        dispatch(listComplete(logins));
      },
      (resp: Response) => {
        notifications.notifyError(resp);
      }
    );
}

export function doRevoke(dispatch: BenchlingDispatch, login: WarehouseLogin): void {
  dispatch(revokeStart(login));
  WarehouseDAO.revokeLogin(login).then(
    () => {
      notifications.success('Credentials have been revoked.');
    },
    (resp: Response) => {
      notifications.notifyError(resp);
      dispatch(revokeFail(login));
    }
  );
}

export function revokeLogin(login: WarehouseLogin): Thunk<void> {
  return (dispatch: BenchlingDispatch) => {
    const msg = 'Are you sure you want to revoke these credentials? They cannot be restored.';
    new popup.Confirm({title: 'Confirm Revoking Credential', msg})
      .choice('Confirm', 'btn-af-danger', () => {
        doRevoke(dispatch, login);
      })
      .choice('Cancel', 'btn-af-default')
      .show();
  };
}

interface WarehouseCreateLoginModalOpenAction {
  type: WarehouseActionType.WAREHOUSE_CREATE_LOGIN_MODAL_OPEN;
}

export function openCreateLoginModal(): WarehouseCreateLoginModalOpenAction {
  return {
    type: WarehouseActionType.WAREHOUSE_CREATE_LOGIN_MODAL_OPEN,
  };
}

interface WarehouseCreateLoginModalCloseAction {
  type: WarehouseActionType.WAREHOUSE_CREATE_LOGIN_MODAL_CLOSE;
}

export function closeCreateLoginModal(): WarehouseCreateLoginModalCloseAction {
  return {
    type: WarehouseActionType.WAREHOUSE_CREATE_LOGIN_MODAL_CLOSE,
  };
}

interface WarehouseCreateLoginLabelUpdateAction {
  type: WarehouseActionType.WAREHOUSE_CREATE_LOGIN_LABEL_UPDATE;
  label: string;
}

export function updateCreateLoginLabel(label: string): WarehouseCreateLoginLabelUpdateAction {
  return {
    type: WarehouseActionType.WAREHOUSE_CREATE_LOGIN_LABEL_UPDATE,
    label,
  };
}

interface WarehouseCreateLoginModalCreateStartAction {
  type: WarehouseActionType.WAREHOUSE_CREATE_LOGIN_MODAL_CREATE_START;
}

function createLoginStart(): WarehouseCreateLoginModalCreateStartAction {
  return {
    type: WarehouseActionType.WAREHOUSE_CREATE_LOGIN_MODAL_CREATE_START,
  };
}

interface WarehouseCreateLoginModalCreateCompleteAction {
  type: WarehouseActionType.WAREHOUSE_CREATE_LOGIN_MODAL_CREATE_COMPLETE;
  login: CreatedWarehouseLogin;
}

function createLoginComplete(login: CreatedWarehouseLogin): WarehouseCreateLoginModalCreateCompleteAction {
  return {
    type: WarehouseActionType.WAREHOUSE_CREATE_LOGIN_MODAL_CREATE_COMPLETE,
    login,
  };
}

interface WarehouseCreateLoginModalCreateFailAction {
  type: WarehouseActionType.WAREHOUSE_CREATE_LOGIN_MODAL_CREATE_FAIL;
}

function createLoginFail(): WarehouseCreateLoginModalCreateFailAction {
  return {
    type: WarehouseActionType.WAREHOUSE_CREATE_LOGIN_MODAL_CREATE_FAIL,
  };
}

export function createLogin(label: string): Thunk<void> {
  return (dispatch: BenchlingDispatch) => {
    dispatch(createLoginStart());
    return WarehouseDAO.createLogin(label).then(
      (login: CreatedWarehouseLogin) => {
        dispatch(createLoginComplete(login));
      },
      (resp: Response) => {
        notifications.notifyError(resp);
        dispatch(createLoginFail());
      }
    );
  };
}

export async function createUserDefinedView(
  viewName: string,
  viewQuery: string,
  viewDescription: string,
  originAnalyticsQueryApiIdentifier: string
): Promise<void> {
  try {
    await WarehouseDAO.createUserDefinedView({
      view_name: viewName,
      view_query: viewQuery,
      view_description: viewDescription,
      origin_analytics_query_api_identifier: originAnalyticsQueryApiIdentifier,
    });
    notifications.success(
      `Initiated creation of user-defined view "${viewName}". View should be available in a few minutes.`
    );
  } catch (error) {
    if (net.isServerRequestError(error)) {
      if (error.responseJSON?.userMessage) {
        throw new CreateUserDefinedViewException(error.responseJSON.userMessage);
      } else {
        throw new CreateUserDefinedViewException(
          'An error occurred while creating the view. Please try again, and contact Benchling Support if the issue persists'
        );
      }
    } else {
      throw error;
    }
  }
}

export type WarehouseAction =
  | WarehouseLoginListCompleteAction
  | WarehouseLoginRevokeStartAction
  | WarehouseLoginRevokeFailAction
  | WarehouseCreateLoginModalOpenAction
  | WarehouseCreateLoginModalCloseAction
  | WarehouseCreateLoginLabelUpdateAction
  | WarehouseCreateLoginModalCreateStartAction
  | WarehouseCreateLoginModalCreateCompleteAction
  | WarehouseCreateLoginModalCreateFailAction;
